class VariantRadios extends HTMLElement {
  constructor() {
    super();
    this.addEventListener('change', this.onVariantChange);
    // on option click, close the drawer
    this.querySelectorAll('input:not(:disabled').forEach((input) =>
      input.addEventListener('click', this.closeDrawer)
    );

    this.updateOptions();
    this.updateMasterId();
    this.highlightOptions();
  }

  onVariantChange() {
    this.updateOptions();
    this.updateMasterId();
    this.toggleAddButton(true, '', false);

    if (!this.currentVariant) {
      this.toggleAddButton(true, '', true);
      this.setUnavailable();
    } else {
      this.updateMedia();
      this.updateURL();
      this.updateVariantInput();
      this.renderProductInfo();
      this.dispatchVariantChange();
    }

    this.highlightOptions();
  }

  /**
   * closeDrawer
   * @description Close drawer and remove the group classes for each option
   */
  closeDrawer() {
    const body = document.querySelector('body');
    const bodyDrawerMainClass = 'product-options-drawer-is-open';
    if (body.classList.contains(bodyDrawerMainClass)) {
      body.classList.remove(
        'product-options-1',
        'product-options-2',
        'product-options-3',
        bodyDrawerMainClass
      );
    }
  }

  updateOptions() {
    const fieldsets = Array.from(this.querySelectorAll('fieldset'));
    this.options = fieldsets.map((fieldset) => {
      let checkedInput = Array.from(fieldset.querySelectorAll('input')).find(
        (radio) => radio.checked
      );
      // add guard in case the user enters a link of a variant that is not available for the current market
      if (!checkedInput) {
        checkedInput = fieldset.querySelector('input:not(:disabled)');
        checkedInput.checked = true;
        this.onVariantChange();
      }

      return checkedInput.value;
    });
  }

  updateMasterId() {
    this.currentVariant = this.getVariantData().find((variant) => {
      return !variant.options
        .map((option, index) => {
          if (!this.options[index]) {
            return true;
          }
          return this.options[index] === option;
        })
        .includes(false);
    });
  }

  dispatchVariantChange() {
    document.dispatchEvent(
      new CustomEvent('variant-change', {
        bubbles: true,
        variant: this.currentVariant,
        detail: {
          variant: this.currentVariant,
        },
      })
    );
  }

  updateMedia() {
    if (!this.currentVariant || !this.currentVariant?.featured_media) return;
    const newMedia = document.querySelector(
      `[data-media-id="${this.dataset.section}-${this.currentVariant.featured_media.id}"]`
    );
    if (!newMedia) return;
    const parent = newMedia.parentElement;
    parent.prepend(newMedia);
    window.setTimeout(() => {
      parent.scroll(0, 0);
    });
  }

  updateURL() {
    if (!this.currentVariant) return;
    window.history.replaceState({}, '', `${this.dataset.url}?variant=${this.currentVariant.id}`);
  }

  updateVariantInput() {
    const productForms = document.querySelectorAll(
      `#product-form-${this.dataset.section}, #product-form-installment`
    );
    productForms.forEach((productForm) => {
      const input = productForm.querySelector('input[name="id"]');
      input.value = this.currentVariant.id;
      input.dispatchEvent(new Event('change', { bubbles: true }));
    });
  }

  renderProductInfo() {
    fetch(
      `${this.dataset.url}?variant=${this.currentVariant.id}&section_id=${this.dataset.section}`
    )
      .then((response) => response.text())
      .then((responseText) => {
        const html = new DOMParser().parseFromString(responseText, 'text/html');

        // Update the Product prices (compare at price and full price) in the main source (data attributes) & Product summary
        this.updatePrices.bind(this, html)();

        document.getElementById(`price-${this.dataset.section}`)?.classList.remove('hidden');
        if (this.currentVariant) {
          this.toggleAddButton(!this.currentVariant.available, window.variantStrings.soldOut);
        }

        // Update the inventory quantity hidden input
        const inventoryQtyDestination = document
          .querySelector(`#product-form-${this.dataset.section}`)
          .querySelector('input[name="inventory_quantity"]');
        const inventoryQtySource = html
          .querySelector(`#product-form-${this.dataset.section}`)
          .querySelector('input[name="inventory_quantity"]');
        inventoryQtyDestination.value = inventoryQtySource.value;

        // Update the selected value & price for each option in the Product summary (variants)
        const currentValueDestination = document.querySelectorAll('[data-current-option-value]');
        const currentValueSource = html.querySelectorAll('[data-current-option-value]');
        currentValueDestination.forEach((valueDestination, index) => {
          if (valueDestination && currentValueSource[index])
            valueDestination.innerHTML = currentValueSource[index].innerHTML;
        });

        const currentPriceDestination = document.querySelector('[data-current-option-price]');
        const currentPriceSource = html.querySelector('[data-current-option-price]');
        if (currentPriceSource && currentPriceDestination)
          currentPriceDestination.innerHTML = currentPriceSource.innerHTML;

        // Update the Product gallery and Product summary (selected upsell products triggers & drawer)
        const upsellInformationDestination = document.querySelector(
          '[data-upsell-products-selector]'
        );
        const upsellInformationSource = html.querySelector('[data-upsell-products-selector]');
        if (upsellInformationSource && upsellInformationDestination) {
          this.updateUpsellContent.bind(
            this,
            upsellInformationSource,
            upsellInformationDestination
          )();
        }
      });
  }

  /**
   * toggleAddButton
   * @param {Boolean} disable
   * @param {String} text
   * @param {Boolean} modifyClass
   * @returns {void}
   */
  toggleAddButton(disable = true, text, modifyClass = true) {
    const addButton = document
      .getElementById(`product-form-${this.dataset.section}`)
      ?.querySelector('[name="add"]');
    const addToCartButtonText = addButton.querySelector('[data-add-to-cart-button-text]');

    if (!addButton || !addToCartButtonText) return;

    const isEdit = sessionStorage.productEdit;

    if (disable) {
      addButton.setAttribute('disabled', true);
      if (text && !isEdit) addToCartButtonText.textContent = text;
    } else {
      addButton.removeAttribute('disabled');
      if (!isEdit) addToCartButtonText.textContent = window.variantStrings.addToCart;
    }

    if (!modifyClass) return;
  }

  setUnavailable() {
    const addButton = document
      .getElementById(`product-form-${this.dataset.section}`)
      ?.querySelector('[name="add"]');
    if (!addButton) return;
    addButton.textContent = window.variantStrings.unavailable;
    document.getElementById(`price-${this.dataset.section}`)?.classList.add('hidden');
  }

  getVariantData() {
    this.variantData =
      this.variantData || JSON.parse(this.querySelector('[type="application/json"]').textContent);
    return this.variantData;
  }

  /*
   * Check for each option value if the other one/two selected variant options are available
   */
  highlightOptions() {
    // If no selected element make all available
    let selectedElement = event
      ? event.target
      : this.querySelector('[data-option-group] input[checked]');
    if (!selectedElement) {
      this.querySelectorAll('[data-option-group]').forEach((option) => {
        option.querySelectorAll('[data-option]').forEach((optionEl) => {
          let label = optionEl.querySelector('[data-option-label]');
          label.classList.remove('option__label--disabled');
        });
      });
      return;
    }

    // Loop over option groups
    this.querySelectorAll('[data-option-group]').forEach((option) => {
      // Get index from option group (1, 2 or 3)
      const optionIndex = parseInt(option.dataset.optionGroup);

      // Create compare options from group, remove selected option based on index
      const compareOptions = [...this.options].filter((option, index) => index != optionIndex);

      // Loop over possible values for option group
      option.querySelectorAll('[data-option]').forEach((optionEl) => {
        let label = optionEl.querySelector('[data-option-label]'),
          input = optionEl.querySelector('[data-option-input]'),
          value = input.value;

        // Match option availability and check if the two current options are available for value
        let match = this.getVariantData().filter((variant) => {
          // Get available variants where the current option index is the option value
          if (variant.options[optionIndex] == value && variant.available) {
            // Create variant options, remove option based on option value
            const variantOptions = [...variant.options].filter(
              (option, index) => index != optionIndex
            );

            // Check if compare options is the same as variant options
            return variantOptions.every(
              (item, index) => item.toLowerCase() === compareOptions[index].toLowerCase()
            );
          }

          return false;
        });

        // Debug current index, current value, compare options and match
        // console.log(optionIndex, value, compareOptions, match);

        // Enable disable variant options
        label.classList.toggle('option__label--disabled', match.length == 0);
      });
    });
  }

  /**
   * updatePrices
   * @description Update the Product prices (compare at price and full price) in the main source (data attributes) & Product summary on Variant change
   * @param {Object} html
   */
  updatePrices(html) {
    const productInfoSource = html.querySelector('product-info');
    const productInfoDestination = document.querySelector('product-info');

    // update the compare at price
    const compareFormattedPriceSource = productInfoSource.dataset.compareFormattedPrice;
    if (compareFormattedPriceSource != undefined) {
      // update the data attributes
      if (productInfoDestination.dataset.compareFormattedPrice != undefined) {
        productInfoDestination.dataset.compareFormattedPrice = compareFormattedPriceSource;
      }
      if (
        productInfoDestination.dataset.comparePrice != undefined &&
        productInfoSource.dataset.comparePrice != undefined
      ) {
        productInfoDestination.dataset.comparePrice = productInfoSource.dataset.comparePrice;
      }

      // update in the Product Summary
      const comparePriceSummaryDestination = productInfoDestination.querySelector(
        '[data-summary-compare-price]'
      );
      if (comparePriceSummaryDestination) {
        comparePriceSummaryDestination.innerHTML = compareFormattedPriceSource;
      }
    }

    // update the total price
    const totalFormattedPriceSource = productInfoSource.dataset.totalFormattedPrice;
    if (totalFormattedPriceSource != undefined) {
      // update the data attributes
      if (productInfoDestination.dataset.totalFormattedPrice != undefined) {
        productInfoDestination.dataset.totalFormattedPrice = totalFormattedPriceSource;
        productInfoDestination.dataset.totalPrice = productInfoSource.dataset.totalPrice;
      }

      // update in the Product Summary and Add to cart button
      const totalPriceSummaryDestination = productInfoDestination.querySelector(
        '[data-summary-total-price]'
      );
      const totalPriceAddToCartDestination = productInfoDestination.querySelector(
        '[data-add-to-cart-button-price]'
      );
      const totalProductFormattedPriceDestinations = [
        totalPriceSummaryDestination,
        totalPriceAddToCartDestination,
      ];

      totalProductFormattedPriceDestinations.forEach((priceDestination) => {
        priceDestination && (priceDestination.innerHTML = totalFormattedPriceSource);
      });
    }
  }

  /**
   * updateUpsellContent
   * @description Update the Product gallery and Product summary (selected upsell products triggers & drawer) on Variant change
   * @param {Object} upsellInformationSource
   * @param {Object} upsellInformationDestination
   */
  updateUpsellContent(upsellInformationSource, upsellInformationDestination) {
    const productMediaImagesWrapper = document.querySelector('[data-product-media-images-wrapper]');

    // Update the max-width CSS variable for the resizing according to the Size option selected
    if (this.querySelector('[data-product-image-max-widths]')) {
      // get the selected size input
      const selectedInput = Array.from(
        this.querySelector('[data-size-fieldset]').querySelectorAll('input')
      ).find((radio) => radio.checked);

      if (!selectedInput && !productMediaImagesWrapper) return;

      // get the max-widths (mobile & desktop) for the selected variant
      const maxImageWidths = JSON.parse(selectedInput.dataset.productImageMaxWidths);
      const { imgMaxWidthPercentage, imgMaxWidthPercentageMd } = maxImageWidths;

      // update the max-width CSS variables
      if (imgMaxWidthPercentage) {
        productMediaImagesWrapper.style.setProperty(
          '--img-max-width-percentage',
          imgMaxWidthPercentage
        );
      }
      if (imgMaxWidthPercentageMd) {
        productMediaImagesWrapper.style.setProperty(
          '--img-max-width-percentage-md',
          imgMaxWidthPercentageMd
        );
      }

      // slide to the 1st slide of the product media slider
      if (imgMaxWidthPercentage || imgMaxWidthPercentageMd) {
        this.productMediaSlider = document.querySelector('product-media-slider');
        this.productMediaSlider.goToSlide(0);
      }
    }

    // Get all the previously selected upsell products and the corresponding Product IDs before updating the content
    const selectedUpsellProducts = Array.from(
      upsellInformationDestination.querySelectorAll('input')
    ).filter((radio) => radio.checked);
    const upsellProductIDs = selectedUpsellProducts.map(
      (upsellProduct) => upsellProduct.dataset.upsellProductId
    );

    // update the upsell information
    upsellInformationDestination.innerHTML = upsellInformationSource.innerHTML;

    // check the previous selected upsell products
    for (let i = 0; i < upsellProductIDs.length; i++) {
      const upsellProductInput = upsellInformationDestination.querySelector(
        `[data-upsell-product-id="${upsellProductIDs[i]}"]`
      );

      // uncheck the passe-partout option if there's no frame selected and break the loop
      if (i == 0 && !upsellProductInput) {
        const passePartoutUpsell = upsellInformationDestination.querySelector(
          `[data-upsell-product-id="${upsellProductIDs[1]}"]`
        );
        if (passePartoutUpsell) passePartoutUpsell.checked = false;
        break;
      }

      if (!upsellProductInput) return;

      upsellProductInput.checked = true;
      this.productUpsellProducts = document.querySelector('product-upsell-products');
      this.productUpsellProducts.handleUpsellSelectionChange.bind(this.productUpsellProducts, {
        target: upsellProductInput,
      })();
    }

    // Update the upsell images in the gallery

    if (!productMediaImagesWrapper) return;

    // show/hide frame accordingly
    const selectedFrame = upsellInformationDestination.querySelector(
      '[data-upsell-product="frame"]:not([data-upsell-none]):checked'
    );
    productMediaImagesWrapper
      .querySelector('.product-media-slider__frame')
      .classList.toggle('opacity-0', !selectedFrame);

    // hide shadow if frame is selected
    productMediaImagesWrapper
      .querySelector('.product-media-slider__shadow')
      ?.classList.toggle('opacity-0', selectedFrame);

    // show/hide passe-partout accordingly
    const passePartoutImage = productMediaImagesWrapper.querySelector(
      '.product-media-slider__passe-partout'
    );

    if (!passePartoutImage) return;

    const selectedPassePartout = upsellInformationDestination.querySelector(
      '[data-upsell-product="passe-partout"]:not([data-upsell-none]):checked'
    );

    productMediaImagesWrapper.classList.toggle(
      'product-media-slider--has-passe-partout',
      selectedFrame && selectedPassePartout
    );
    if (selectedFrame && selectedPassePartout) {
      passePartoutImage.classList.remove('opacity-0');
    } else {
      passePartoutImage.classList.add('opacity-0');
    }
  }
}

if (!customElements.get('variant-radios')) {
  customElements.define('variant-radios', VariantRadios);
}
